import type { Button } from '@/types/contentstack/button';
import type { ComponentSharedButton as TilComponentSharedButton } from '@/types/til-cms/types';
import type {
  ComponentSharedButton,
  ComponentSharedButtonWithIcons,
} from '@/types/tis-cms/types';

export type StrapiButtonSource =
  | ComponentSharedButton
  | ComponentSharedButtonWithIcons
  | TilComponentSharedButton;

type StrapiButtonSourceField =
  | Array<StrapiButtonSource | null | undefined>
  | StrapiButtonSource
  | null
  | undefined;

const SAFE_STRAPI_BUTTON_PROTOCOLS = new Set([
  'http:',
  'https:',
  'mailto:',
  'tel:',
]);
const STRAPI_BUTTON_URL_PLACEHOLDER_BASE =
  'https://strapi-relative-url.invalid';

export const isSafeStrapiButtonProtocol = (protocol: string): boolean =>
  SAFE_STRAPI_BUTTON_PROTOCOLS.has(protocol);

const resolveStrapiButtonUrl = (
  url: string,
  baseUrl?: string
): string | undefined => {
  const explicitUrl = URL.parse(url);
  if (explicitUrl && isSafeStrapiButtonProtocol(explicitUrl.protocol)) {
    return explicitUrl.href;
  }
  if (explicitUrl) {
    return undefined;
  }

  const impliedUrl = URL.parse(`https://${url}`);
  if (impliedUrl && impliedUrl.hostname.includes('.')) {
    return impliedUrl.href;
  }

  const relativeUrl = URL.parse(
    url,
    baseUrl || STRAPI_BUTTON_URL_PLACEHOLDER_BASE
  );
  if (!relativeUrl) {
    return undefined;
  }
  if (baseUrl) {
    return relativeUrl.href;
  }
  return `${relativeUrl.pathname}${relativeUrl.search}${relativeUrl.hash}`;
};

export const normalizeStrapiButton = (
  button: StrapiButtonSource | null | undefined,
  baseUrl?: string
): Button | undefined => {
  if (!button?.url) {
    return undefined;
  }

  const resolvedUrl = resolveStrapiButtonUrl(button.url, baseUrl);
  if (!resolvedUrl) {
    return undefined;
  }

  const props: Button = {
    label: button.label ?? '',
    url: resolvedUrl,
  };

  if (button.target) {
    props.target = button.target;
  }

  if ('icon' in button && button.icon) {
    props.icon = button.icon;
  }

  return props;
};

const toStrapiButtonSourceArray = (
  buttons: StrapiButtonSourceField
): Array<StrapiButtonSource | null | undefined> => {
  if (Array.isArray(buttons)) {
    return buttons;
  }
  if (buttons) {
    return [buttons];
  }
  return [];
};

export const normalizeStrapiButtons = (
  buttons: StrapiButtonSourceField,
  baseUrl?: string
): Button[] => {
  const mapped: Button[] = [];

  for (const item of toStrapiButtonSourceArray(buttons)) {
    const button = normalizeStrapiButton(item, baseUrl);
    if (button) {
      mapped.push(button);
    }
  }

  return mapped;
};
